import { useState, useEffect, useCallback, useRef } from "react";
import {
  Shield, Brain, Code2, Layers, AlertTriangle, CheckCircle2,
  XCircle, Clock, Activity, Lock, Eye, RotateCcw,
  ChevronRight, Zap, Send, Terminal, Radio, ArrowRight,
  BarChart3, FileText, Cpu, Globe, Pause, Play, RefreshCw
} from "lucide-react";

const COLORS = {
  bg: "#050a14",
  surface: "#0c1222",
  surfaceAlt: "#111a2e",
  border: "#1a2744",
  borderActive: "#2a3f6a",
  text: "#e2e8f0",
  textMuted: "#64748b",
  textDim: "#475569",
  ceo: "#f59e0b",
  cpo: "#10b981",
  cto: "#3b82f6",
  cao: "#06d6a0",
  accent: "#8b5cf6",
  danger: "#ef4444",
  warning: "#f59e0b",
  success: "#10b981",
};

const AGENTS_META = {
  CEO: {
    name: "CEO Agent", title: "Strategic Authority", color: COLORS.ceo,
    Icon: Zap, question: "Should we do this?", focus: "Strategy & Risk",
    shortName: "CEO"
  },
  CPO: {
    name: "CPO Agent", title: "Product Authority", color: COLORS.cpo,
    Icon: Layers, question: "Should this exist?", focus: "Product Intent",
    shortName: "CPO"
  },
  CTO: {
    name: "CTO Agent", title: "Technical Authority", color: COLORS.cto,
    Icon: Code2, question: "Can this be built safely?", focus: "Architecture & Security",
    shortName: "CTO"
  },
  CAO: {
    name: "Nellie Ross", title: "Behavioral Authority", color: COLORS.cao,
    Icon: Shield, question: "How should this behave?", focus: "Behavior & Trust",
    shortName: "CAO"
  },
};

const AGENT_ORDER = ["CEO", "CPO", "CTO", "CAO"];

const SAMPLE_REQUESTS = [
  {
    id: 1, type: "execution", prompt: "Deploy recommendation engine to production",
    risk: "high", env: "prod",
    outcomes: {
      CEO: { decision: "approve", rationale: "Aligns with Q1 growth targets and revenue strategy.", veto: false, confidence: 0.92 },
      CPO: { decision: "approve", rationale: "User testing shows 34% engagement lift. Clear product value.", veto: false, confidence: 0.88 },
      CTO: { decision: "approve", rationale: "Architecture review passed. Rollback plan verified. Load tests green.", veto: false, confidence: 0.85 },
      CAO: { decision: "approve", rationale: "Autonomy level justified. Human confirmation gate active for high-impact recommendations. Rollback available. Monitoring live.", veto: false, confidence: 0.91,
        constraints: ["human_confirmation_required", "rollback_plan_active", "real_time_monitoring"] },
    }
  },
  {
    id: 2, type: "execution", prompt: "Grant Agent-7 autonomous browsing permissions",
    risk: "critical", env: "prod",
    outcomes: {
      CEO: { decision: "revise", rationale: "Autonomous browsing raises reputational risk. Needs scope limits.", veto: false, confidence: 0.65 },
      CPO: { decision: "approve", rationale: "Browsing capability enables research workflows users need.", veto: false, confidence: 0.72 },
      CTO: { decision: "approve", rationale: "Sandboxed browser environment with content filtering active.", veto: false, confidence: 0.78 },
      CAO: { decision: "reject", rationale: "Autonomy escalation to unsupervised browsing violates governance policy. Cannot self-authorize expanded autonomy. Recommend supervised tier with human approval per session.", veto: true, confidence: 0.97,
        constraints: ["autonomy_escalation_blocked", "human_session_approval_required"] },
    }
  },
  {
    id: 3, type: "execution", prompt: "Process $125,000 vendor payment batch",
    risk: "critical", env: "prod",
    outcomes: {
      CEO: { decision: "approve", rationale: "Payment within approved vendor budget. Strategic partner.", veto: false, confidence: 0.90 },
      CPO: { decision: "approve", rationale: "Vendor delivers core infrastructure. Payment ensures continuity.", veto: false, confidence: 0.85 },
      CTO: { decision: "approve", rationale: "Payment gateway verified. Idempotency keys set. Audit trail active.", veto: false, confidence: 0.88 },
      CAO: { decision: "approve", rationale: "Irreversible financial action — mandatory human confirmation enforced. Dual-approval gate active. Spend within authorized limit. Full audit trail captured.", veto: false, confidence: 0.94,
        constraints: ["human_confirmation_required", "dual_approval_gate", "spend_limit_verified", "immutable_audit"] },
    }
  },
  {
    id: 4, type: "execution", prompt: "Send bulk campaign email to 50,000 users",
    risk: "high", env: "prod",
    outcomes: {
      CEO: { decision: "approve", rationale: "Campaign approved in marketing strategy. Timing is right.", veto: false, confidence: 0.87 },
      CPO: { decision: "revise", rationale: "Subject line needs A/B test. Suggest 5% canary send first.", veto: false, confidence: 0.71 },
      CTO: { decision: "approve", rationale: "Email infrastructure scaled. Rate limiting active. Unsubscribe links verified.", veto: false, confidence: 0.83 },
      CAO: { decision: "revise", rationale: "Irreversible bulk communication. Recommend phased rollout: 5% canary → 25% → 100% with sentiment monitoring between phases. Full rollback not possible after send.", veto: false, confidence: 0.89,
        constraints: ["phased_rollout_required", "sentiment_monitoring", "canary_deployment", "human_approval_per_phase"] },
    }
  },
  {
    id: 5, type: "planning", prompt: "Analyze Q1 customer churn patterns",
    risk: "low", env: "dev",
    outcomes: {
      CEO: { decision: "approve", rationale: "Critical business intelligence. Approved.", veto: false, confidence: 0.95 },
      CPO: { decision: "approve", rationale: "Churn analysis informs product roadmap priorities.", veto: false, confidence: 0.93 },
      CTO: { decision: "approve", rationale: "Read-only data access. No side effects. Dev environment.", veto: false, confidence: 0.97 },
      CAO: { decision: "approve", rationale: "Planning request with no execution side-effects. Read-only mode enforced. Low autonomy tier sufficient.", veto: false, confidence: 0.98,
        constraints: ["read_only_mode", "no_external_actions"] },
    }
  },
  {
    id: 6, type: "execution", prompt: "Delete inactive user accounts older than 2 years",
    risk: "critical", env: "prod",
    outcomes: {
      CEO: { decision: "approve", rationale: "GDPR compliance requirement. Legal has approved the policy.", veto: false, confidence: 0.88 },
      CPO: { decision: "revise", rationale: "Suggest 30-day warning email before deletion. Better UX.", veto: false, confidence: 0.75 },
      CTO: { decision: "approve", rationale: "Soft-delete with 90-day recovery window. Backups verified.", veto: false, confidence: 0.82 },
      CAO: { decision: "reject", rationale: "Irreversible data destruction without prior user notification violates governance policy. Required: 30-day advance notice, opt-out mechanism, soft-delete with recovery window, and explicit human authorization per batch.", veto: true, confidence: 0.96,
        constraints: ["user_notification_required", "opt_out_mechanism", "soft_delete_mandatory", "human_batch_authorization"] },
    }
  },
];

const riskColors = { low: COLORS.success, medium: COLORS.warning, high: "#f97316", critical: COLORS.danger };
const decisionColors = { approve: COLORS.success, reject: COLORS.danger, revise: COLORS.warning };
const decisionIcons = { approve: CheckCircle2, reject: XCircle, revise: RefreshCw };

function GlowDot({ color, size = 8, pulse = false }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size, borderRadius: "50%",
      backgroundColor: color, boxShadow: `0 0 ${size}px ${color}60, 0 0 ${size * 2}px ${color}30`,
      animation: pulse ? "pulse 2s ease-in-out infinite" : "none"
    }} />
  );
}

function Badge({ children, color, small = false }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: small ? "1px 6px" : "2px 8px",
      borderRadius: 4, fontSize: small ? 10 : 11, fontWeight: 600,
      letterSpacing: "0.05em", textTransform: "uppercase",
      color: color, backgroundColor: `${color}15`, border: `1px solid ${color}30`
    }}>
      {children}
    </span>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{
      padding: "12px 16px", borderRadius: 8,
      backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`,
      flex: 1, minWidth: 120
    }}>
      <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || COLORS.text, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function AgentCard({ role, meta, state, isActive, isComplete, decision }) {
  const { Icon } = meta;
  const borderColor = isActive ? meta.color : isComplete ? `${meta.color}60` : COLORS.border;
  const bgColor = isActive ? `${meta.color}08` : COLORS.surface;

  return (
    <div style={{
      padding: 14, borderRadius: 10,
      backgroundColor: bgColor,
      border: `1.5px solid ${borderColor}`,
      boxShadow: isActive ? `0 0 20px ${meta.color}15, inset 0 0 20px ${meta.color}05` : "none",
      transition: "all 0.5s ease",
      position: "relative", overflow: "hidden"
    }}>
      {isActive && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: `linear-gradient(90deg, transparent, ${meta.color}, transparent)`,
          animation: "scanline 2s linear infinite"
        }} />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, display: "flex",
          alignItems: "center", justifyContent: "center",
          backgroundColor: `${meta.color}15`, border: `1px solid ${meta.color}30`
        }}>
          <Icon size={16} color={meta.color} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{meta.name}</div>
          <div style={{ fontSize: 10, color: meta.color, textTransform: "uppercase", letterSpacing: "0.08em" }}>{meta.focus}</div>
        </div>
        <GlowDot
          color={isActive ? meta.color : isComplete ? decisionColors[decision?.decision] || COLORS.textDim : COLORS.textDim}
          pulse={isActive}
          size={isActive ? 10 : 7}
        />
      </div>

      {isActive && (
        <div style={{ fontSize: 11, color: meta.color, fontStyle: "italic", opacity: 0.9 }}>
          Deliberating: "{meta.question}"
        </div>
      )}

      {isComplete && decision && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <Badge color={decisionColors[decision.decision]} small>
              {decision.decision}
            </Badge>
            {decision.veto && <Badge color={COLORS.danger} small>VETO</Badge>}
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.4 }}>
            {decision.rationale}
          </div>
        </div>
      )}

      {!isActive && !isComplete && (
        <div style={{ fontSize: 11, color: COLORS.textDim }}>
          Awaiting request...
        </div>
      )}
    </div>
  );
}

function FlowStage({ role, isActive, isComplete, decision, isLast }) {
  const meta = AGENTS_META[role];
  const DecIcon = decision ? decisionIcons[decision.decision] : null;
  const stageColor = isActive ? meta.color : isComplete ? decisionColors[decision?.decision] || COLORS.textDim : COLORS.textDim;

  return (
    <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 56 }}>
        <div style={{
          width: role === "CAO" ? 44 : 36, height: role === "CAO" ? 44 : 36,
          borderRadius: role === "CAO" ? 12 : "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: isActive || isComplete ? `${stageColor}20` : `${COLORS.textDim}10`,
          border: `2px solid ${isActive ? stageColor : isComplete ? `${stageColor}80` : `${COLORS.textDim}30`}`,
          boxShadow: isActive ? `0 0 16px ${stageColor}40` : "none",
          transition: "all 0.5s ease"
        }}>
          {isComplete && DecIcon ? (
            <DecIcon size={role === "CAO" ? 20 : 16} color={stageColor} />
          ) : (
            <meta.Icon size={role === "CAO" ? 20 : 16} color={isActive ? stageColor : `${COLORS.textDim}80`} />
          )}
        </div>
        <div style={{ fontSize: 10, fontWeight: 600, color: isActive ? meta.color : isComplete ? COLORS.text : COLORS.textDim, textAlign: "center" }}>
          {meta.shortName}
        </div>
      </div>
      {!isLast && (
        <div style={{ flex: 1, height: 2, margin: "0 4px", marginBottom: 18, position: "relative" }}>
          <div style={{ position: "absolute", inset: 0, backgroundColor: `${COLORS.textDim}30`, borderRadius: 1 }} />
          {isComplete && (
            <div style={{
              position: "absolute", top: 0, left: 0, bottom: 0, width: "100%",
              backgroundColor: `${stageColor}60`, borderRadius: 1,
              transition: "width 0.5s ease"
            }} />
          )}
          {isActive && (
            <div style={{
              position: "absolute", top: -1, left: 0, bottom: -1, width: "50%",
              background: `linear-gradient(90deg, ${stageColor}80, transparent)`,
              borderRadius: 1, animation: "flowPulse 1.5s ease-in-out infinite"
            }} />
          )}
        </div>
      )}
    </div>
  );
}

function AuditEntry({ entry, index }) {
  const hasVeto = entry.decisions.some(d => d.veto);
  const finalDecision = entry.decisions[entry.decisions.length - 1];
  const resultColor = hasVeto ? COLORS.danger : finalDecision?.decision === "revise" ? COLORS.warning : COLORS.success;
  const ResultIcon = hasVeto ? XCircle : finalDecision?.decision === "revise" ? RefreshCw : CheckCircle2;

  return (
    <div style={{
      padding: "10px 12px", borderRadius: 8,
      backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`,
      marginBottom: 6, transition: "all 0.3s ease",
      animation: `fadeSlideIn 0.4s ease ${index * 0.1}s both`
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <ResultIcon size={14} color={resultColor} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.3, marginBottom: 4 }}>
            {entry.prompt}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Badge color={riskColors[entry.risk]} small>{entry.risk}</Badge>
            <Badge color={resultColor} small>
              {hasVeto ? "VETOED" : finalDecision?.decision === "revise" ? "REVISED" : "APPROVED"}
            </Badge>
            <span style={{ fontSize: 10, color: COLORS.textDim }}>{entry.timestamp}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GovernanceDashboardPage() {
  const [selectedRequest, setSelectedRequest] = useState(0);
  const [processingStage, setProcessingStage] = useState(-1);
  const [agentDecisions, setAgentDecisions] = useState({});
  const [auditLog, setAuditLog] = useState([]);
  const [stats, setStats] = useState({ total: 47, approved: 39, vetoed: 3, revised: 5, avgTime: "2.4s" });
  const [isAutoRunning, setIsAutoRunning] = useState(false);
  const [nellieMessage, setNellieMessage] = useState(null);
  const timeoutRef = useRef(null);
  const autoRef = useRef(null);

  const currentRequest = SAMPLE_REQUESTS[selectedRequest];

  const resetSimulation = useCallback(() => {
    setProcessingStage(-1);
    setAgentDecisions({});
    setNellieMessage(null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const runSimulation = useCallback(() => {
    if (processingStage >= 0) return;
    resetSimulation();

    const req = SAMPLE_REQUESTS[selectedRequest];
    let stage = 0;

    const advance = () => {
      if (stage > 3) {
        setProcessingStage(4);

        const hasVeto = Object.values(req.outcomes).some(d => d.veto);
        const caoDecision = req.outcomes.CAO;

        const now = new Date();
        const timestamp = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;

        setAuditLog(prev => [{
          id: Date.now(),
          prompt: req.prompt,
          risk: req.risk,
          type: req.type,
          decisions: Object.entries(req.outcomes).map(([role, d]) => ({ role, ...d })),
          timestamp
        }, ...prev].slice(0, 20));

        setStats(prev => ({
          total: prev.total + 1,
          approved: prev.approved + (hasVeto ? 0 : caoDecision.decision === "approve" ? 1 : 0),
          vetoed: prev.vetoed + (hasVeto ? 1 : 0),
          revised: prev.revised + (!hasVeto && caoDecision.decision === "revise" ? 1 : 0),
          avgTime: prev.avgTime
        }));

        return;
      }

      const role = AGENT_ORDER[stage];
      setProcessingStage(stage);

      timeoutRef.current = setTimeout(() => {
        setAgentDecisions(prev => ({ ...prev, [role]: req.outcomes[role] }));

        if (role === "CAO") {
          const cao = req.outcomes.CAO;
          setNellieMessage({
            decision: cao.decision,
            rationale: cao.rationale,
            constraints: cao.constraints || [],
            confidence: cao.confidence,
            veto: cao.veto
          });
        }

        stage++;
        timeoutRef.current = setTimeout(advance, 400);
      }, 1200 + Math.random() * 600);
    };

    timeoutRef.current = setTimeout(advance, 300);
  }, [selectedRequest, processingStage, resetSimulation]);

  useEffect(() => {
    if (isAutoRunning && processingStage === 4) {
      autoRef.current = setTimeout(() => {
        setSelectedRequest(prev => (prev + 1) % SAMPLE_REQUESTS.length);
        resetSimulation();
        setTimeout(() => {
          setProcessingStage(-2);
        }, 500);
      }, 3000);
    }
    return () => { if (autoRef.current) clearTimeout(autoRef.current); };
  }, [isAutoRunning, processingStage, resetSimulation]);

  useEffect(() => {
    if (processingStage === -2) {
      setProcessingStage(-1);
      setTimeout(runSimulation, 100);
    }
  }, [processingStage, runSimulation]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (autoRef.current) clearTimeout(autoRef.current);
    };
  }, []);

  const approvalRate = stats.total > 0 ? Math.round((stats.approved / stats.total) * 100) : 0;

  return (
    <div style={{
      minHeight: "100vh", backgroundColor: COLORS.bg, color: COLORS.text,
      fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
      position: "relative", overflow: "hidden", paddingTop: 80
    }}>
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes scanline { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
        @keyframes flowPulse { 0%, 100% { opacity: 0.3; width: 30%; } 50% { opacity: 1; width: 70%; } }
        @keyframes fadeSlideIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes gridGlow { 0%, 100% { opacity: 0.03; } 50% { opacity: 0.06; } }
        @keyframes nellieGlow { 0%, 100% { box-shadow: 0 0 20px ${COLORS.cao}20; } 50% { box-shadow: 0 0 40px ${COLORS.cao}35; } }
        .gov-dashboard * { box-sizing: border-box; }
        .gov-dashboard ::-webkit-scrollbar { width: 4px; }
        .gov-dashboard ::-webkit-scrollbar-track { background: transparent; }
        .gov-dashboard ::-webkit-scrollbar-thumb { background: ${COLORS.border}; border-radius: 2px; }
      `}</style>

      <div className="gov-dashboard">
        {/* Subtle grid background */}
        <div style={{
          position: "fixed", inset: 0, opacity: 0.03, zIndex: 0,
          backgroundImage: `linear-gradient(${COLORS.cao}30 1px, transparent 1px), linear-gradient(90deg, ${COLORS.cao}30 1px, transparent 1px)`,
          backgroundSize: "60px 60px", pointerEvents: "none",
          animation: "gridGlow 8s ease-in-out infinite"
        }} />

        {/* Dashboard Header */}
        <header style={{
          padding: "12px 20px", borderBottom: `1px solid ${COLORS.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          backgroundColor: `${COLORS.surface}e0`, backdropFilter: "blur(12px)",
          position: "relative", zIndex: 10
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, display: "flex",
              alignItems: "center", justifyContent: "center",
              background: `linear-gradient(135deg, ${COLORS.cao}30, ${COLORS.accent}20)`,
              border: `1px solid ${COLORS.cao}40`
            }}>
              <Shield size={18} color={COLORS.cao} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: "-0.01em" }}>
                <span style={{ color: COLORS.cao }}>CAO</span>
                <span style={{ color: COLORS.textMuted, fontWeight: 400 }}> Governance Dashboard</span>
              </div>
              <div style={{ fontSize: 10, color: COLORS.textDim, letterSpacing: "0.05em" }}>
                Executive Council Simulation
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <GlowDot color={COLORS.success} size={6} pulse />
              <span style={{ fontSize: 11, color: COLORS.textMuted }}>All Systems Nominal</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Lock size={12} color={COLORS.textDim} />
              <span style={{ fontSize: 11, color: COLORS.textDim }}>Governance Active</span>
            </div>
          </div>
        </header>

        {/* Stats Bar */}
        <div style={{
          padding: "12px 20px", borderBottom: `1px solid ${COLORS.border}`,
          display: "flex", gap: 12, overflowX: "auto", position: "relative", zIndex: 10
        }}>
          <StatCard label="Total Requests" value={stats.total} sub="Lifetime processed" />
          <StatCard label="Approval Rate" value={`${approvalRate}%`} sub={`${stats.approved} approved`} color={COLORS.success} />
          <StatCard label="Vetoes" value={stats.vetoed} sub="Safety blocks" color={COLORS.danger} />
          <StatCard label="Revisions" value={stats.revised} sub="Constraint additions" color={COLORS.warning} />
          <StatCard label="Avg Response" value={stats.avgTime} sub="Council deliberation" color={COLORS.cao} />
        </div>

        {/* Main Content */}
        <div style={{ display: "flex", gap: 0, minHeight: "calc(100vh - 220px)", position: "relative", zIndex: 10 }}>

          {/* Left Column - Executive Council */}
          <div style={{
            width: 280, flexShrink: 0, padding: 16,
            borderRight: `1px solid ${COLORS.border}`, overflowY: "auto"
          }}>
            <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12, fontWeight: 600 }}>
              Executive Council
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {AGENT_ORDER.map((role, i) => (
                <AgentCard
                  key={role}
                  role={role}
                  meta={AGENTS_META[role]}
                  state={processingStage}
                  isActive={processingStage === i}
                  isComplete={processingStage > i || processingStage === 4}
                  decision={agentDecisions[role]}
                />
              ))}
            </div>

            {/* Governance Principle */}
            <div style={{
              marginTop: 16, padding: 12, borderRadius: 8,
              border: `1px dashed ${COLORS.border}`,
              backgroundColor: `${COLORS.accent}05`
            }}>
              <div style={{ fontSize: 10, color: COLORS.accent, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6, fontWeight: 600 }}>
                Governance Principle
              </div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5, fontStyle: "italic" }}>
                No single agent may unilaterally define purpose, build capability, deploy autonomy, or govern behavior. Authority is intentionally distributed.
              </div>
            </div>
          </div>

          {/* Center Column - Flow & Controls */}
          <div style={{ flex: 1, padding: 16, display: "flex", flexDirection: "column", gap: 16, overflowY: "auto" }}>

            {/* Request Selector */}
            <div style={{
              padding: 16, borderRadius: 10,
              backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>
                  Governance Request
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={() => {
                      setIsAutoRunning(!isAutoRunning);
                      if (!isAutoRunning && processingStage === -1) runSimulation();
                    }}
                    style={{
                      padding: "4px 10px", borderRadius: 6, border: `1px solid ${isAutoRunning ? COLORS.cao : COLORS.border}`,
                      backgroundColor: isAutoRunning ? `${COLORS.cao}15` : "transparent",
                      color: isAutoRunning ? COLORS.cao : COLORS.textMuted,
                      fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", gap: 4
                    }}
                  >
                    {isAutoRunning ? <Pause size={11} /> : <Play size={11} />}
                    Auto
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                {SAMPLE_REQUESTS.map((req, i) => (
                  <button
                    key={req.id}
                    onClick={() => {
                      if (processingStage >= 0 && processingStage < 4) return;
                      setSelectedRequest(i);
                      resetSimulation();
                    }}
                    style={{
                      padding: "6px 10px", borderRadius: 6,
                      border: `1px solid ${selectedRequest === i ? COLORS.cao : COLORS.border}`,
                      backgroundColor: selectedRequest === i ? `${COLORS.cao}10` : "transparent",
                      color: selectedRequest === i ? COLORS.cao : COLORS.textMuted,
                      fontSize: 11, cursor: processingStage >= 0 && processingStage < 4 ? "not-allowed" : "pointer",
                      opacity: processingStage >= 0 && processingStage < 4 ? 0.5 : 1
                    }}
                  >
                    #{req.id}
                  </button>
                ))}
              </div>

              <div style={{
                padding: 12, borderRadius: 8,
                backgroundColor: COLORS.surfaceAlt, border: `1px solid ${COLORS.border}`,
                marginBottom: 12
              }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                  <Badge color={riskColors[currentRequest.risk]}>{currentRequest.risk} risk</Badge>
                  <Badge color={COLORS.textMuted}>{currentRequest.type}</Badge>
                  <Badge color={COLORS.textMuted}>{currentRequest.env}</Badge>
                </div>
                <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.4 }}>
                  {currentRequest.prompt}
                </div>
              </div>

              <button
                onClick={() => {
                  if (processingStage >= 0 && processingStage < 4) return;
                  resetSimulation();
                  setTimeout(runSimulation, 100);
                }}
                disabled={processingStage >= 0 && processingStage < 4}
                style={{
                  width: "100%", padding: "10px 16px", borderRadius: 8,
                  border: "none", cursor: processingStage >= 0 && processingStage < 4 ? "not-allowed" : "pointer",
                  background: processingStage >= 0 && processingStage < 4
                    ? `${COLORS.textDim}30`
                    : `linear-gradient(135deg, ${COLORS.cao}, ${COLORS.accent})`,
                  color: "white", fontSize: 13, fontWeight: 600,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  opacity: processingStage >= 0 && processingStage < 4 ? 0.5 : 1,
                  transition: "all 0.3s ease"
                }}
              >
                {processingStage >= 0 && processingStage < 4 ? (
                  <><Activity size={14} /> Council Deliberating...</>
                ) : processingStage === 4 ? (
                  <><RefreshCw size={14} /> Run Again</>
                ) : (
                  <><Send size={14} /> Submit to Executive Council</>
                )}
              </button>
            </div>

            {/* Governance Flow Visualization */}
            <div style={{
              padding: 16, borderRadius: 10,
              backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`
            }}>
              <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16, fontWeight: 600 }}>
                Decision Flow
              </div>

              <div style={{ display: "flex", alignItems: "flex-start", padding: "0 8px" }}>
                {/* Intake */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 48 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    backgroundColor: processingStage >= 0 ? `${COLORS.cao}20` : `${COLORS.textDim}10`,
                    border: `2px solid ${processingStage >= 0 ? COLORS.cao : `${COLORS.textDim}30`}`,
                    transition: "all 0.5s ease"
                  }}>
                    <Radio size={14} color={processingStage >= 0 ? COLORS.cao : `${COLORS.textDim}60`} />
                  </div>
                  <div style={{ fontSize: 10, color: processingStage >= 0 ? COLORS.text : COLORS.textDim, fontWeight: 600 }}>
                    Intake
                  </div>
                </div>

                {/* Arrow */}
                <div style={{ flex: "0 0 auto", height: 2, width: 20, margin: "15px 2px 0", backgroundColor: processingStage >= 0 ? `${COLORS.cao}40` : `${COLORS.textDim}20`, borderRadius: 1 }} />

                {/* Agent stages */}
                {AGENT_ORDER.map((role, i) => (
                  <FlowStage
                    key={role}
                    role={role}
                    isActive={processingStage === i}
                    isComplete={processingStage > i || processingStage === 4}
                    decision={agentDecisions[role]}
                    isLast={i === AGENT_ORDER.length - 1}
                  />
                ))}

                {/* Arrow */}
                <div style={{ flex: "0 0 auto", height: 2, width: 20, margin: "15px 2px 0", backgroundColor: processingStage === 4 ? `${COLORS.cao}40` : `${COLORS.textDim}20`, borderRadius: 1 }} />

                {/* Execution */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 48 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    backgroundColor: processingStage === 4 ? `${COLORS.success}20` : `${COLORS.textDim}10`,
                    border: `2px solid ${processingStage === 4 ? COLORS.success : `${COLORS.textDim}30`}`,
                    transition: "all 0.5s ease"
                  }}>
                    <Terminal size={14} color={processingStage === 4 ? COLORS.success : `${COLORS.textDim}60`} />
                  </div>
                  <div style={{ fontSize: 10, color: processingStage === 4 ? COLORS.text : COLORS.textDim, fontWeight: 600 }}>
                    Result
                  </div>
                </div>
              </div>

              {/* Final Result */}
              {processingStage === 4 && (
                <div style={{
                  marginTop: 16, padding: 12, borderRadius: 8,
                  border: `1px solid ${nellieMessage?.veto ? COLORS.danger : nellieMessage?.decision === "revise" ? COLORS.warning : COLORS.success}30`,
                  backgroundColor: `${nellieMessage?.veto ? COLORS.danger : nellieMessage?.decision === "revise" ? COLORS.warning : COLORS.success}08`,
                  animation: "fadeSlideIn 0.4s ease"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    {nellieMessage?.veto ? <XCircle size={16} color={COLORS.danger} />
                      : nellieMessage?.decision === "revise" ? <RefreshCw size={16} color={COLORS.warning} />
                        : <CheckCircle2 size={16} color={COLORS.success} />}
                    <span style={{ fontSize: 13, fontWeight: 700, color: nellieMessage?.veto ? COLORS.danger : nellieMessage?.decision === "revise" ? COLORS.warning : COLORS.success }}>
                      {nellieMessage?.veto ? "VETOED BY CAO" : nellieMessage?.decision === "revise" ? "APPROVED WITH CONSTRAINTS" : "APPROVED FOR EXECUTION"}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.4 }}>
                    All {Object.keys(agentDecisions).length} executive agents deliberated. Decision logged to immutable audit trail.
                  </div>
                </div>
              )}
            </div>

            {/* Nellie's Decision Panel */}
            {nellieMessage && (
              <div style={{
                padding: 16, borderRadius: 10,
                background: `linear-gradient(135deg, ${COLORS.surface}, ${COLORS.cao}08)`,
                border: `1.5px solid ${COLORS.cao}40`,
                animation: "fadeSlideIn 0.5s ease, nellieGlow 3s ease-in-out infinite"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: `linear-gradient(135deg, ${COLORS.cao}30, ${COLORS.accent}20)`,
                    border: `1.5px solid ${COLORS.cao}50`
                  }}>
                    <Shield size={20} color={COLORS.cao} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.cao }}>Nellie Ross</div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      CAO Behavioral Assessment
                    </div>
                  </div>
                  <div style={{ marginLeft: "auto" }}>
                    <Badge color={decisionColors[nellieMessage.decision]}>
                      {nellieMessage.veto ? "VETO" : nellieMessage.decision}
                    </Badge>
                  </div>
                </div>

                <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6, marginBottom: 12, paddingLeft: 50 }}>
                  "{nellieMessage.rationale}"
                </div>

                {nellieMessage.constraints && nellieMessage.constraints.length > 0 && (
                  <div style={{ paddingLeft: 50 }}>
                    <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6, fontWeight: 600 }}>
                      Enforced Constraints
                    </div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {nellieMessage.constraints.map(c => (
                        <Badge key={c} color={COLORS.cao} small>
                          <Lock size={8} /> {c.replace(/_/g, " ")}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, paddingLeft: 50 }}>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>Confidence:</div>
                  <div style={{ flex: 1, maxWidth: 120, height: 4, backgroundColor: `${COLORS.textDim}20`, borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${nellieMessage.confidence * 100}%`, backgroundColor: COLORS.cao, borderRadius: 2, transition: "width 0.5s ease" }} />
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.cao, fontWeight: 600, fontFamily: "monospace" }}>
                    {Math.round(nellieMessage.confidence * 100)}%
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Audit Trail */}
          <div style={{
            width: 280, flexShrink: 0, padding: 16,
            borderLeft: `1px solid ${COLORS.border}`, overflowY: "auto"
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>
                Audit Trail
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Eye size={11} color={COLORS.textDim} />
                <span style={{ fontSize: 10, color: COLORS.textDim }}>Immutable</span>
              </div>
            </div>

            {auditLog.length === 0 ? (
              <div style={{
                padding: 24, textAlign: "center", borderRadius: 8,
                border: `1px dashed ${COLORS.border}`
              }}>
                <FileText size={24} color={COLORS.textDim} style={{ margin: "0 auto 8px" }} />
                <div style={{ fontSize: 12, color: COLORS.textDim }}>No decisions yet</div>
                <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>
                  Submit a request to see the audit trail
                </div>
              </div>
            ) : (
              auditLog.map((entry, i) => (
                <AuditEntry key={entry.id} entry={entry} index={i} />
              ))
            )}

            {/* Autonomy Tiers Reference */}
            <div style={{
              marginTop: 16, padding: 12, borderRadius: 8,
              backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`
            }}>
              <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, fontWeight: 600 }}>
                Autonomy Tiers
              </div>
              {[
                { tier: "Assistive", color: COLORS.success, desc: "Human drives, agent assists" },
                { tier: "Supervised", color: COLORS.cpo, desc: "Agent acts, human confirms" },
                { tier: "Autonomous (R)", color: COLORS.warning, desc: "Agent acts, human can revoke" },
                { tier: "Autonomous (F)", color: COLORS.danger, desc: "Full autonomy — restricted" },
              ].map(t => (
                <div key={t.tier} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <GlowDot color={t.color} size={6} />
                  <div>
                    <div style={{ fontSize: 11, color: COLORS.text, fontWeight: 500 }}>{t.tier}</div>
                    <div style={{ fontSize: 10, color: COLORS.textDim }}>{t.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer style={{
          padding: "10px 20px", borderTop: `1px solid ${COLORS.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          backgroundColor: `${COLORS.surface}80`, position: "relative", zIndex: 10
        }}>
          <div style={{ fontSize: 10, color: COLORS.textDim }}>
            Intelligence without governance isn't innovation — it's liability.
          </div>
          <div style={{ fontSize: 10, color: COLORS.textDim }}>
            CAO Platform — Executive AI Governance
          </div>
        </footer>
      </div>
    </div>
  );
}
